import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../auth/AuthContext';
import { fetchMyUploads } from '../services/experiencesFirestore';
import AuthPanel from './AuthPanel';
import BentoGrid from './BentoGrid';

const Container = styled.div`
  min-height: 100vh;
  padding: 20px;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  padding: 8px 16px;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  &:hover {
    border-color: rgba(255, 255, 255, 0.4);
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  background: linear-gradient(135deg, #ff00aa, #00f5ff);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 24px;
  font-size: 0.95rem;
`;

const StatusTabs = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const StatusTab = styled.button`
  background: ${(p) => (p.$active ? 'rgba(0, 245, 255, 0.25)' : 'rgba(255,255,255,0.06)')};
  border: 1px solid ${(p) => (p.$active ? '#00f5ff' : 'rgba(255,255,255,0.2)')};
  color: #fff;
  padding: 8px 16px;
  border-radius: 999px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  &:hover {
    border-color: rgba(255, 255, 255, 0.4);
  }
`;

const EmptyState = styled.p`
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  font-size: 1.1rem;
  margin-top: 40px;
`;

function MyUploadsPage({
  onBack,
  onExperienceClick,
  onNavigateToSaved,
  onNavigateToRecommendations,
  onNavigateToCreate,
  onNavigateToMyUploads,
  onNavigateToReview,
  canCreatePost,
  canReview
}) {
  const { user } = useAuth();
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'approved' | 'pending'

  useEffect(() => {
    if (!user) {
      setUploads([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchMyUploads(user.uid)
      .then(setUploads)
      .catch(() => setUploads([]))
      .finally(() => setLoading(false));
  }, [user]);

  const filtered = filter === 'all' ? uploads : uploads.filter((u) => u.status === filter);

  return (
    <Container>
      <TopBar>
        <BackButton type="button" onClick={onBack}>
          ← Back
        </BackButton>
        <AuthPanel
          onNavigateToSaved={onNavigateToSaved}
          onNavigateToRecommendations={onNavigateToRecommendations}
          onNavigateToCreate={onNavigateToCreate}
          onNavigateToMyUploads={onNavigateToMyUploads}
          onNavigateToReview={onNavigateToReview}
          canCreatePost={canCreatePost}
          canReview={canReview}
        />
      </TopBar>
      <Title>My uploads</Title>
      <Subtitle>Content you’ve submitted. Approved items appear on the home page.</Subtitle>

      <StatusTabs>
        <StatusTab $active={filter === 'all'} onClick={() => setFilter('all')}>
          All
        </StatusTab>
        <StatusTab $active={filter === 'pending'} onClick={() => setFilter('pending')}>
          Pending review
        </StatusTab>
        <StatusTab $active={filter === 'approved'} onClick={() => setFilter('approved')}>
          Approved
        </StatusTab>
      </StatusTabs>

      {loading && <EmptyState>Loading…</EmptyState>}
      {!loading && filtered.length === 0 && (
        <EmptyState>
          {uploads.length === 0
            ? "You haven't uploaded any content yet. Use Create post to add an experience."
            : `No ${filter === 'all' ? '' : filter} uploads.`}
        </EmptyState>
      )}
      {!loading && filtered.length > 0 && (
        <BentoGrid
          experiences={filtered}
          onExperienceClick={onExperienceClick}
          showSaveButton={false}
        />
      )}
    </Container>
  );
}

export default MyUploadsPage;
